<template>
  <div :class="classes" v-if="srcSet" ref="container">
    <!-- <div v-if="!isLoaded" class="loader-container"></div> -->
    <picture v-if="!imageCrop">
      <source
        media="(min-width:1025px)"
        type="image/webp"
        :srcset="srcSet"
        :sizes="`(max-width: 1023px) 100vw, ${containerWidth}px`"
      />
      <source type="image/webp" :srcset="mobileSrcSet" sizes="100vw" />
      <img
        decoding="async"
        :src="`${srcLg}`"
        :alt="altText"
        :loading="loading"
        :class="imageClasses"
        @load="handleImageLoaded()"
        ref="imageElement"
      />
    </picture>
    <img
      v-else
      decoding="async"
      :src="`${getOriginalImage()}`"
      :srcset="originalSrcSet"
      :alt="altText"
      :loading="loading"
      :class="imageClasses"
      @load="handleImageLoaded()"
      ref="imageElement"
    />
  </div>
</template>

<script setup lang="ts">
import { toRefs, computed, ref, watch, onMounted } from "vue";
import { Alias } from "@/util/constants";
import { buildImageUrls } from "@/util/buildImageUrls";
import { getImageWidth } from "@/util/getImageWidth";

export interface ImageSizes {
  dXl: string;
  dLg: string;
  dMd: string;
  mLg: string;
  mMd: string;
  mSm: string;
  listXl: string;
  listLg: string;
  listMd: string;
  mlistLg: string;
  mlistMd: string;
  mlistSm: string;
}

export interface ImageProps {
  alt?: string;
  portrait?: boolean;
  classes?: string;
  loading?: "lazy" | "eager";
  isLink?: boolean;
  imageCrop?: boolean;
  image: string;
  isList?: boolean;
}

const props = withDefaults(defineProps<ImageProps>(), {
  alt: "",
  loading: "lazy",
  portrait: false,
  fullViewHeight: false,
  isUmbraco: false,
  isList: false,
  imageCrop: false,
});
const { loading, alt, classes, isLink, image, isList, imageCrop } = toRefs(props);
const isLoaded = ref(false);
const container = ref<HTMLDivElement | null>(null);
const containerWidth = ref(0);

onMounted(() => {
  if (container.value) {
    containerWidth.value = container.value.clientWidth;
  }
});

const handleImageLoaded = () => {
  isLoaded.value = true;
};

const altText = computed(() => {
  return alt?.value || "";
});

const imageSizes = computed(() => {
  if (!image?.value) return;
  const img = JSON.parse(image?.value);

  return getImageWidth(img);
});

const getOriginalImage = () => {
  if (!image?.value) return;

  const img = JSON.parse(image?.value);
  return `${img.url}?width=${img.width}`;
};

const imageCrops = computed(() => {
  if (!image?.value) return;
  const img = JSON.parse(image?.value);
  return buildImageUrls(img);
});

//Original image srcset
const originalSrcSet = computed(() => {
  const img = JSON.parse(image?.value);
  if (!srcSm.value || !srcMd.value || !srcLg.value) return;
  return `${img.url}?width=${sizeSm.value} ${sizeSm.value}w, ${img.url}?width=${sizeMd.value} ${sizeMd.value}w, ${img.url}?width=${sizeLg.value} ${sizeLg.value}w`;
});

// Image Sizes
const sizeMobileSm = computed(() => {
  if (!imageSizes?.value) return;
  return `${
    isList.value ? imageSizes.value[Alias.mlistSm] : imageSizes.value[Alias.mSm]
  }`;
});

const sizeMobileMd = computed(() => {
  if (!imageSizes?.value) return;
  return `${
    isList.value ? imageSizes.value[Alias.mlistMd] : imageSizes.value[Alias.mMd]
  }`;
});

const sizeMobileLg = computed(() => {
  if (!imageSizes?.value) return;
  return `${
    isList.value ? imageSizes.value[Alias.mlistLg] : imageSizes.value[Alias.mLg]
  }`;
});

const sizeSm = computed(() => {
  if (!imageSizes?.value) return;
  return `${isList.value ? imageSizes.value[Alias.listMd] : imageSizes.value[Alias.dMd]}`;
});

const sizeMd = computed(() => {
  if (!imageSizes?.value) return;
  return `${isList.value ? imageSizes.value[Alias.listLg] : imageSizes.value[Alias.dLg]}`;
});

const sizeLg = computed(() => {
  if (!imageSizes?.value) return;
  return `${isList.value ? imageSizes.value[Alias.listXl] : imageSizes.value[Alias.dXl]}`;
});

// Image crops
const srcMobileSm = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.mlistSm] : imageCrops.value[Alias.mSm];
});

const srcMobileMd = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.mlistMd] : imageCrops.value[Alias.mMd];
});

const srcMobileLg = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.mlistLg] : imageCrops.value[Alias.mLg];
});

const srcSm = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.listMd] : imageCrops.value[Alias.dMd];
});

const srcMd = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.listLg] : imageCrops.value[Alias.dLg];
});

const srcLg = computed(() => {
  if (!imageCrops?.value) return;
  return isList.value ? imageCrops.value[Alias.listXl] : imageCrops.value[Alias.dXl];
});

const srcSet = computed(() => {
  if (!srcSm.value || !srcMd.value || !srcLg.value) return;
  return `${srcSm.value} ${sizeSm.value}w, ${srcMd.value} ${sizeMd.value}w, ${srcLg.value} ${sizeLg.value}w`;
});

const mobileSrcSet = computed(() => {
  if (!srcMobileSm.value || !srcMobileMd.value || !srcMobileLg.value) return;
  return `${srcMobileSm.value} ${sizeMobileSm.value}w, ${srcMobileMd.value} ${sizeMobileMd.value}w, ${srcMobileLg.value} ${sizeMobileLg.value}w`;
});

// Images that are links should have a scale hover effect
const linkTransitions = computed(() => {
  return isLink.value ? "group-hover:scale-105" : "";
});

const imageClasses = computed(() => {
  return `${linkTransitions?.value} transition-transform duration-500 ease-out-cubic ${classes?.value}`;
});
</script>
